import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formMaxWidth } from '../constants/general-info-constants';
import {
  getFeedstockSource,
  postFeedstockSource
} from '../../../../../../../actions/rof-burns/01-project-info/01-general-info';
import tooltips from '../../../../../../../store/tooltips';
import GeneralInfoForm from '../shared-components/GeneralInfoForm';

const feedstockSourceTypes = [
  'Hardwood',
  'Softwood',
  'Mixed',
  'Agricultural Waste'
]

const associatedMeasurementUnits = {
  'Imperial': [
    'Tonnes', 'Acres'
  ],
  'Metric': [
    'Tonnes', 'Hectares'
  ]
}

const defaultFormState = {
  feedstockType: '',
  feedstockDetails: '',
  location: '',
  feedstockAmount: null,
  feedstockUnit: '',
}

export default function FeedstockSource({ projectId, selectedCategory }) {
  const dispatch = useDispatch()
  const [formState, setFormState] = useState(defaultFormState)
  const [formErrors, setFormErrors] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const { selectedProject } = useSelector((state) => (state.postFormData));
  const projectMeasurementSystem = selectedProject?.measureType
  const derivedUnitsArray = associatedMeasurementUnits[projectMeasurementSystem]

  const feedstockSourceData = useSelector((state) => (state.generalInfoReducer.feedstockSource?.data));

  useEffect(() => {
    handleFormErrorReset()
    dispatch(getFeedstockSource(projectId))
  }, [selectedCategory, projectId])

  useEffect(() => {
    if (feedstockSourceData)
      setFormState({
        feedstockType: feedstockSourceData?.feedstock_type,
        feedstockDetails: feedstockSourceData?.feedstock_details,
        location: feedstockSourceData?.location,
        feedstockAmount: feedstockSourceData?.feedstock_amount,
        feedstockUnit: feedstockSourceData?.feedstock_unit,
      })
    else {
      setFormState(defaultFormState)
    }

  }, [selectedCategory, projectId, feedstockSourceData])

  const feedstockSourceQuestions = (formState, derivedUnitsArray) => {
    const { feedstockType, feedstockDetails, location, feedstockAmount, feedstockUnit } = formState
    const validFeedstockUnit = derivedUnitsArray?.includes(feedstockUnit) ? feedstockUnit : '';

    return [
      {
        type: 'select',
        fieldId: 'feedstockType',
        text: 'Select primary feedstock type:',
        value: feedstockType,
        options:
          feedstockSourceTypes,
        selectPlaceholder: 'Primary Feedstock Type',
        tooltip: tooltips.planningQ1,
      },
      {
        type: 'text',
        fieldId: 'feedstockDetails',
        text: 'Please detail the Woody Biomass Species that will be used:',
        value: feedstockDetails,
        placeholder: 'Please describe your feedstock in terms of volumes and composition of mix of species - if applicable',
        tooltip: tooltips.planningQ2,
      },
      {
        type: 'text',
        fieldId: 'location',
        text: 'Location:',
        value: location,
        placeholder: 'Enter what3words.com location here',
        alert: true,
        tooltip: tooltips.planningQ3,
      },
      {
        type: 'text',
        fieldId: 'feedstockAmount',
        text: 'Please specify the quantity of feedstock available:',
        value: feedstockAmount,
        placeholder: 'Enter quantity',
        tooltip: tooltips.planningQ4,
      },
      {
        type: 'select',
        fieldId: 'feedstockUnit',
        text: 'Select the appropriate unit that the quantity of feedstock was measured in:',
        value: validFeedstockUnit,
        options:
          derivedUnitsArray,
        selectPlaceholder: 'Unit',
      },
    ]
  }

  const onSubmit = async () => {
    try {
      setIsLoading(true)
      const submissionForm = { ...formState };
      validateFeedstockSource(submissionForm)

      await dispatch(postFeedstockSource(submissionForm, projectId))
      await dispatch(getFeedstockSource(projectId))

    } catch (error) {
      setFormErrors(error.message)
    } finally { setIsLoading(false) }
  }

  const setData = (event) => {
    const { name, type } = event.target;
    let value = event.target.value

    if (type === 'file') {
      value = event.target.files[0]
    }

    setFormState(prevState => ({
      ...prevState,
      [name]: value,
    }));
  }

  function validateFeedstockSource(form) {
    // backend expects a number
    const amount = form.feedstockAmount
    if (amount === '') {
      // if empty, reset to null
      form.feedstockAmount = null
    }
    else if (amount !== null && (isNaN(parseFloat(amount)))) {
      throw Error("Please specify a real number for the quantity of feedstock.")
    }
  }

  const handleFormErrorReset = () => {
    setFormErrors(null);
  };

  const questions = feedstockSourceQuestions(formState, derivedUnitsArray)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: formMaxWidth }}>
      <Typography variant='h4' sx={{ pb: 2 }}>
        {selectedCategory}
      </Typography>
      <GeneralInfoForm
        formObjectArray={questions}
        formState={formState}
        formErrors={formErrors}
        isLoading={isLoading}
        onChange={setData}
        onSubmit={onSubmit} />
    </Box>
  )
}